<template>
    <div 
        :class="[
            local_chart.type !== 'table' && local_chart.type !== 'multi_dimension_table' && local_chart.type !== 'scorecard' && local_chart.type !== 'information_card'
                ? 'chart-template' 
                : 'table-template',
            'position-relative'
        ]" 
        :data-chart-id="id"
    >
        <!-- X-Large Layout: Chart (col-8 or col-12) + Side Panel (col-4, optional). Below 992px panel wraps to next row. -->
        <div v-if="current_size === 'x-large' && local_chart.type !== 'information_card' && local_chart.type !== 'table' && local_chart.type !== 'multi_dimension_table'" class="row g-0">
            <!-- Chart Section (col-12 when panel closed, col-12 col-lg-8 when panel open so chart full-width when stacked) -->
            <div :class="sidePanelVisible ? 'col-12 col-lg-8' : 'col-12'">
                <div class="chart-content-wrapper">
                    <div class="chart-header d-flex justify-content-between align-items-start">
                        <!-- Title Section with Slot Support -->
                        <div v-if="show_title && local_chart.type !== 'information_card' && local_chart.type !== 'scorecard'" class="chart-title-section flex-grow-1">
                            <slot name="title" :chart="local_chart" :formatted-title="getFormattedTitle" :formatted-subtitle="getFormattedSubtitle">
                                <!-- Default title rendering -->
                                <div v-if="local_chart.type === 'scorecard'">
                                    <p class="mx-2 mt-2 mb-0" v-if="displayTitle != null && displayTitle.trim() != ''">{{ getFormattedTitle(displayTitle) }}</p>
                                </div>
                                <div v-else>
                                    <div v-if="!if_benchmark_report" class="row">
                                        <div class="d-flex align-items-center flex-wrap">
                                            <h3 class="chart-title my-1" v-if="displayTitle != null && displayTitle != ''">
                                                {{ displayTitle }}
                                                <!-- <sup>
                                                    <img class="info-icon mt-2 ms-0" src="/img/icons/avid-icons/info.svg" v-show="current_size !== 'x-large' && local_chart.subtitle && local_chart.subtitle.trim() != ''" data-bs-toggle="tooltip" :data-bs-original-title="local_chart.subtitle" :aria-label="local_chart.subtitle" alt="">
                                                </sup> -->
                                            </h3>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <div class="d-flex align-items-center gap-3 flex-wrap">
                                            <h3 class="chart-title my-1" v-if="displayTitle != null && displayTitle != ''">
                                                {{ replaceDate(displayTitle) }}
                                                <!-- <sup>
                                                    <img class="info-icon mt-2 ms-0" src="/img/icons/avid-icons/info.svg" v-show="current_size !== 'x-large' && local_chart.subtitle && local_chart.subtitle.trim() != ''" data-bs-toggle="tooltip" :data-bs-original-title="local_chart.subtitle" :aria-label="local_chart.subtitle" alt="">
                                                </sup> -->
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            </slot>
                        </div>
                        <!-- Spacer for when title is hidden but controls are present -->
                        <div v-else-if="local_chart.type !== 'information_card'" class="flex-grow-1"></div>

                        <!-- Unified Control Actions (Pagination, Zoom, Download, Show Panel) -->
                        <div v-if="local_chart.type !== 'information_card'" class="chart-header-controls d-flex align-items-center gap-2">

                            <!-- Show side panel when it was closed (x-large only) -->
                            <button v-if="current_size === 'x-large' && !sidePanelVisible && !hide_xlarge_side_panel"
                                class="btn btn-secondary btn-icon-only-sm position-static" data-bs-toggle="tooltip" title="Show summary panel" @click="showSidePanel($event)">
                                <img class="icon" src="/img/icons/avid-icons/chevron-circle-right.svg" alt="">
                            </button>

                            <!-- Deep Dive Button - Only show for charts smaller than x-large -->
                            <button v-if="allow_deep_dive && current_size !== 'x-large'" 
                                class="btn btn-secondary deep-dive-btn btn-icon-only-sm position-static" data-bs-toggle="tooltip" title="Deep Dive" @click="openDeepDiveModal($event)">
                                <img class="icon" src="/img/icons/avid-icons/deep-dive.svg" alt="">
                            </button>

                            <!-- Export / Download button -->
                            <button v-if="allow_download" 
                                class="btn btn-secondary download-btn btn-icon-only-sm position-static" data-bs-toggle="tooltip" title="Export Chart" @click="downloadChart($event)">
                                <img src="/img/icons/avid-icons/download.svg" class="icon pe-0" alt="">
                            </button>
                        </div>
                    </div>

                    <scorecard-template
                        v-if="local_chart.type === 'scorecard'" 
                        :chart_selected="local_chart" 
                        :if_refreshing="if_refreshing"
                        :is_filter_panel_expanded="is_filter_panel_expanded"
                        :key="'scorecard-' + id"
                    />

                    <multi-metric-scorecard-template
                        v-if="local_chart.type === 'multi_metric_scorecard'"
                        :chart_selected="local_chart"
                        :if_refreshing="if_refreshing"
                        :is_filter_panel_expanded="is_filter_panel_expanded"
                        :key="'multi-metric-scorecard-' + id"
                    />

                    <pie-chart-template
                        v-if="local_chart.type === 'pie'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter" 
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="pieChart"
                        :key="'pie-' + id"
                    />

                    <line-chart-template
                        v-if="local_chart.type === 'line' || local_chart.type === 'cumulative_line'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :is_cumulative="local_chart.type === 'cumulative_line'" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="lineChart"
                        :key="'line-' + id"
                    />

                    <bar-line-combo-chart-template 
                        v-if="local_chart.type === 'bar_line'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="barLineChart"
                        :key="'bar-line-' + id"
                    />

                    <column-chart-template
                        v-if="local_chart.type === 'column'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :is_section_active="is_section_active"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="columnChart"
                        :key="'column-' + id"
                    />

                    <stacked-column-chart-template 
                        v-if="local_chart.type === 'stacked_column'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_hundred_percent="false"
                        :if_refreshing="if_refreshing" 
                        :if_benchmark_report="if_benchmark_report" 
                        :if_on_report_page="if_on_report_page" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="stackedColumnChart"
                        :key="'stacked-column-' + id"
                    />

                    <stacked-column-chart-template 
                        v-if="local_chart.type === '100_percent_stacked_column'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_hundred_percent="true"
                        :if_refreshing="if_refreshing" 
                        :if_on_report_page="if_on_report_page" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        :key="'100-percent-stacked-column-' + id"
                    />

                    <horizontal-bar-chart-template 
                        v-if="local_chart.type === 'horizontal_bar'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="horizontalBarChart"
                        :key="'horizontal-bar-' + id"
                    />

                    <stacked-horizontal-bar-chart-template 
                        v-if="local_chart.type === 'stacked_bar'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_hundred_percent="false"
                        :if_refreshing="if_refreshing" 
                        :if_benchmark_report="if_benchmark_report" 
                        :if_on_report_page="if_on_report_page" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        ref="stackedHorizontalBarChart"
                        :key="'stacked-horizontal-bar-' + id"
                    />

                    <stacked-horizontal-bar-chart-template 
                        v-if="local_chart.type === '100_percent_stacked_bar'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_hundred_percent="true"
                        :if_refreshing="if_refreshing" 
                        :if_benchmark_report="if_benchmark_report" 
                        :if_on_report_page="if_on_report_page" 
                        :applied_chart_filter="applied_chart_filter"
                        :if_show_label="effectiveShowDataLabels" 
                        :size="current_size"
                        :section_id="section_id"
                        :show_compressed_numbers="show_compressed_numbers"
                        :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                        @exploreChart="exploreChart"
                        @update:pagination="handlePaginationUpdate"
                        :key="'100-percent-stacked-horizontal-bar-' + id"
                    />

                    <table-template 
                        v-if="local_chart.type === 'table'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter" 
                        :section_id="section_id"
                        @exploreChart="exploreChart"
                        :key="'table-' + id"
                        ref="tableTemplate"
                    />

                    <multi-dimension-table-template 
                        v-if="local_chart.type === 'multi_dimension_table'" 
                        :chart_selected="local_chart" 
                        :id="local_chart_id" 
                        :if_refreshing="if_refreshing"
                        :if_on_report_page="if_on_report_page" 
                        :if_benchmark_report="if_benchmark_report" 
                        :applied_chart_filter="applied_chart_filter" 
                        :page_dimensions="page_dimensions"
                        :section_id="section_id"
                        @exploreChart="exploreChart"
                        :key="'multi-dimension-table-' + id"
                        ref="multiDimensionTableTemplate"
                    />

                    <information-card-template
                        v-if="local_chart.type === 'information_card'"
                        :chart_selected="local_chart"
                        :key="'information-card-' + id"
                    />
                </div>
            </div>

            <!-- Side Panel Section (1/3 width) - Only for x-large charts when not closed. Below 992px wraps to next row at full width. -->
            <div v-if="current_size === 'x-large' && sidePanelVisible && !hide_xlarge_side_panel && local_chart.type !== 'information_card' && local_chart.type !== 'table' && local_chart.type !== 'multi_dimension_table'" class="col-12 col-lg-4">
                <div class="chart-side-panel h-100 px-3">
                    <!-- Toggle Group + Close button -->
                    <div class="d-flex align-items-center gap-2 w-100 mb-0">
                        <nav v-if="should_show_summary" class="navbar navbar-expand tertiary-tab-bar flex-grow-1">
                        <div class="container-fluid p-0">
                            <div class="collapse navbar-collapse">
                                <ul class="navbar-nav mb-lg-0 w-100 d-flex">
                                    <li class="nav-item flex-fill">
                                        <a 
                                            class="nav-link d-flex flex-nowrap tertiary-tab w-100 justify-content-center align-items-center" 
                                            :class="{ 'tertiary-tab-active': activeTab === 'edna_overview' }"
                                            href="#" 
                                            role="button" 
                                            aria-expanded="false" 
                                            @click.prevent="activeTab = 'edna_overview'"
                                        >
                                            <span class="w-100 text-center d-flex justify-content-center align-items-center lh-1">
                                                <img src="/img/icons/avid-icons/edna.svg" class="icon me-2" alt="">
                                                Avid Overview
                                            </span>
                                        </a>
                                    </li>
                                    <li class="nav-item flex-fill">
                                        <a 
                                            class="nav-link d-flex flex-nowrap tertiary-tab w-100 justify-content-center align-items-center" 
                                            :class="{ 'tertiary-tab-active': activeTab === 'definitions' }"
                                            href="#" 
                                            role="button" 
                                            aria-expanded="false" 
                                            @click.prevent="activeTab = 'definitions'"
                                        >
                                            <span class="w-100 text-center d-flex justify-content-center align-items-center lh-1">Definitions</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </nav>
                        <button ref="closePanelBtn" type="button" class="btn btn-white btn-icon-only-sm flex-shrink-0 ms-auto" data-bs-toggle="tooltip" title="Close summary panel" aria-label="Close summary panel" @click="closeSidePanel">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                        </button>
                    </div>

                    <h4 v-if="!should_show_summary" class="my-3">Definitions</h4>
                    
                    <!-- Edna Overview Content -->
                    <div v-if="should_show_summary && activeTab === 'edna_overview'" class="edna-overview-content my-3">
                        <!-- AI summary loading (x-large chart) - pulsing border like playbook touchpoint -->
                        <div v-if="isChartSummaryLoading" class="chart-summary-loading is-generating d-flex flex-column align-items-center justify-content-center py-4 px-3">
                            <img src="/img/images/loading_edna.gif" class="chart-summary-loading__logo mb-2" alt="">
                            <p class="chart-summary-loading__message mb-0 text-muted">Getting your summary...</p>
                        </div>
                        <!-- AI summary (x-large chart) with chart reference links -->
                        <div v-else-if="chart_summary && chart_summary.summary" class="chart-summary-ai">
                            <h5 class="chart-summary-ai__heading mb-2">What does this mean?</h5>
                            <div class="chart-summary-ai__body" ref="chartSummaryBody" v-html="processedChartSummaryHtml"></div>
                        </div>
                        <!-- No AI summary yet -->
                        <div v-else-if="!chart_summary || !chart_summary.summary" class="text-muted">
                            <p v-if="!if_refreshing && local_chart.data">There is no summary available for this chart.</p>
                        </div>
                        <!-- Two metrics (percent change and highest number) always at bottom when data exists -->
                        <div v-if="ednaOverviewCards.length > 0" class="row g-3 mt-1">
                            <div v-for="(card, index) in ednaOverviewCards" :key="index" class="col-12 col-xxl-6">
                                <div class="edna-scorecard-card">
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <div class="edna-scorecard-icon me-1" :class="card.iconClass">
                                            <img :src="card.iconSrc" class="icon" alt="">
                                        </div>
                                        <div class="edna-scorecard-title">
                                            <span class="badge bg-white px-2 py-1">{{ card.title }}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <h2 class="edna-scorecard-value mb-1">{{ card.value }}</h2>
                                        <p ref="ednaDescRefs" class="edna-scorecard-description mb-0 text-muted" :data-bs-toggle="descriptionTruncated[index] ? 'tooltip' : undefined" :title="descriptionTruncated[index] ? card.description : undefined" data-bs-placement="bottom" :aria-label="card.description">{{ card.description }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="!if_refreshing && local_chart.data && (!chart_summary || !chart_summary.summary)" class="text-muted mt-2">
                            <p class="mb-0">No data available for summary cards.</p>
                        </div>
                    </div>
                    
                    <!-- Definitions Content -->
                    <div v-if="!should_show_summary || activeTab === 'definitions'" class="definitions-content my-3">
                        <div class="definition-item" v-if="local_chart.subtitle && local_chart.subtitle != ''">
                            <h5 class="definition-label">What Question Does This Answer:</h5>
                            <p class="definition-value">{{ getFormattedSubtitle(local_chart.subtitle) }}</p>
                        </div>

                        <div class="definition-item" v-if="local_chart.description && local_chart.description != ''">
                            <h5 class="definition-label">Description:</h5>
                            <p class="definition-value">{{ this.local_chart.description }}</p>
                        </div>

                        <div class="definition-item" v-if="getDimensions().length > 0">
                            <h5 class="definition-label">Dimensions:</h5>
                            <p class="definition-value">{{ getDimensions().join(', ') }}</p>
                        </div>               

                        <div class="definition-item" v-if="getMetrics().length > 0">
                            <h5 class="definition-label">Metrics:</h5>
                            <p class="definition-value">{{ getMetrics().join(', ') }}</p>
                        </div>
                        
                        <div class="definition-item" v-if="getFilters().length > 0">
                            <h5 class="definition-label">Filters:</h5>
                            <ul class="definition-list">
                                <li v-for="(filter, index) in getFilters()" :key="index">{{ filter }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Non-x-large layout: render header normally -->
        <template v-if="current_size !== 'x-large' || local_chart.type === 'information_card' || local_chart.type === 'table' || local_chart.type === 'multi_dimension_table'">
            <div class="chart-header d-flex justify-content-between align-items-start">
                <!-- Title Section with Slot Support -->
                <div v-if="show_title && local_chart.type !== 'information_card' && local_chart.type !== 'scorecard'" class="chart-title-section flex-grow-1">
                    <slot name="title" :chart="local_chart" :formatted-title="getFormattedTitle" :formatted-subtitle="getFormattedSubtitle">
                        <!-- Default title rendering -->
                        <div v-if="local_chart.type === 'scorecard'">
                            <p class="mx-2 mt-2 mb-0" v-if="displayTitle != null && displayTitle.trim() != ''">{{ getFormattedTitle(displayTitle) }}</p>
                        </div>
                        <div v-else>
                            <div v-if="!if_benchmark_report" class="row">
                                <div class="d-flex align-items-center flex-wrap">
                                    <h3 class="chart-title my-1" v-if="displayTitle != null && displayTitle != ''">
                                        {{ displayTitle }}
                                        <!-- <sup>
                                            <img class="info-icon mt-2 ms-0" src="/img/icons/avid-icons/info.svg" v-show="current_size !== 'x-large' && local_chart.subtitle && local_chart.subtitle.trim() != ''" data-bs-toggle="tooltip" :data-bs-original-title="local_chart.subtitle" :aria-label="local_chart.subtitle" alt="">
                                        </sup> -->
                                    </h3>
                                </div>
                            </div>
                            <div v-else>
                                <div class="d-flex align-items-center gap-3 flex-wrap">
                                    <h3 class="chart-title my-1" v-if="displayTitle != null && displayTitle != ''">
                                        {{ replaceDate(displayTitle) }}
                                        <!-- <sup>
                                            <img class="info-icon mt-2 ms-0" src="/img/icons/avid-icons/info.svg" v-show="current_size !== 'x-large' && local_chart.subtitle && local_chart.subtitle.trim() != ''" data-bs-toggle="tooltip" :data-bs-original-title="local_chart.subtitle" :aria-label="local_chart.subtitle" alt="">
                                        </sup> -->
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </slot>
                </div>
                <!-- Spacer for when title is hidden but controls are present -->
                <div v-else-if="local_chart.type !== 'information_card'" class="flex-grow-1"></div>

                <!-- Unified Control Actions (Pagination, Zoom, Download) -->
                <div v-if="local_chart.type !== 'information_card'" class="chart-header-controls d-flex align-items-center gap-2">

                    <!-- Deep Dive Button - Only show for charts smaller than x-large -->
                    <button v-if="allow_deep_dive && (current_size !== 'x-large' || local_chart.type === 'table' || local_chart.type === 'multi_dimension_table')" 
                        class="btn btn-secondary deep-dive-btn btn-icon-only-sm position-static" data-bs-toggle="tooltip" title="Deep Dive" @click="openDeepDiveModal($event)">
                        <img class="icon" src="/img/icons/avid-icons/deep-dive.svg" alt="">
                    </button>

                    <!-- Export / Download button -->
                    <button v-if="allow_download && local_chart.type !== 'scorecard' && local_chart.type !== 'multi_metric_scorecard' && local_chart.type !== 'information_card'" 
                        class="btn btn-secondary download-btn btn-icon-only-sm position-static" data-bs-toggle="tooltip" title="Export Chart" @click="downloadChart($event)">
                        <img src="/img/icons/avid-icons/download.svg" class="icon pe-0" alt="">
                    </button>
                </div>
            </div>
            <!-- Chart templates for non-x-large layout -->
            <scorecard-template
                v-if="local_chart.type === 'scorecard'" 
                :chart_selected="local_chart" 
                :if_refreshing="if_refreshing"
                :is_filter_panel_expanded="is_filter_panel_expanded"
                :key="'scorecard-' + id"
            />

            <multi-metric-scorecard-template
                v-if="local_chart.type === 'multi_metric_scorecard'"
                :chart_selected="local_chart"
                :if_refreshing="if_refreshing"
                :is_filter_panel_expanded="is_filter_panel_expanded"
                :key="'multi-metric-scorecard-' + id"
            />

            <pie-chart-template
                v-if="local_chart.type === 'pie'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter" 
                :if_show_label="show_data_labels"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="pieChart"
                :key="'pie-' + id"
            />

            <line-chart-template
                v-if="local_chart.type === 'line' || local_chart.type === 'cumulative_line'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :is_cumulative="local_chart.type === 'cumulative_line'" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="lineChart"
                :key="'line-' + id"
            />

            <bar-line-combo-chart-template 
                v-if="local_chart.type === 'bar_line'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="barLineChart"
                :key="'bar-line-' + id"
            />

            <column-chart-template
                v-if="local_chart.type === 'column'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :is_section_active="is_section_active"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="columnChart"
                :key="'column-' + id"
            />

            <stacked-column-chart-template 
                v-if="local_chart.type === 'stacked_column'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_hundred_percent="false"
                :if_refreshing="if_refreshing" 
                :if_benchmark_report="if_benchmark_report" 
                :if_on_report_page="if_on_report_page" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="stackedColumnChart"
                :key="'stacked-column-' + id"
            />

            <stacked-column-chart-template 
                v-if="local_chart.type === '100_percent_stacked_column'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_hundred_percent="true"
                :if_refreshing="if_refreshing" 
                :if_on_report_page="if_on_report_page" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                :key="'100-percent-stacked-column-' + id"
            />

            <horizontal-bar-chart-template
                v-if="local_chart.type === 'horizontal_bar'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="horizontalBarChart"
                :key="'horizontal-bar-' + id"
            />

            <stacked-horizontal-bar-chart-template 
                v-if="local_chart.type === 'stacked_bar'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_hundred_percent="false"
                :if_refreshing="if_refreshing" 
                :if_benchmark_report="if_benchmark_report" 
                :if_on_report_page="if_on_report_page" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                ref="stackedHorizontalBarChart"
                :key="'stacked-horizontal-bar-' + id"
            />

            <stacked-horizontal-bar-chart-template 
                v-if="local_chart.type === '100_percent_stacked_bar'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_hundred_percent="true"
                :if_refreshing="if_refreshing" 
                :if_benchmark_report="if_benchmark_report" 
                :if_on_report_page="if_on_report_page" 
                :applied_chart_filter="applied_chart_filter"
                :if_show_label="effectiveShowDataLabels" 
                :size="current_size"
                :section_id="section_id"
                :show_compressed_numbers="show_compressed_numbers"
                :force-show-all-data="current_size === 'x-large' && !sidePanelVisible"
                @exploreChart="exploreChart"
                @update:pagination="handlePaginationUpdate"
                :key="'100-percent-stacked-horizontal-bar-' + id"
            />

            <table-template 
                v-if="local_chart.type === 'table'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter" 
                :section_id="section_id"
                @exploreChart="exploreChart"
                :key="'table-' + id"
                ref="tableTemplate"
            />

            <multi-dimension-table-template 
                v-if="local_chart.type === 'multi_dimension_table'" 
                :chart_selected="local_chart" 
                :id="local_chart_id" 
                :if_refreshing="if_refreshing"
                :if_on_report_page="if_on_report_page" 
                :if_benchmark_report="if_benchmark_report" 
                :applied_chart_filter="applied_chart_filter" 
                :page_dimensions="page_dimensions"
                :section_id="section_id"
                @exploreChart="exploreChart"
                :key="'multi-dimension-table-' + id"
                ref="multiDimensionTableTemplate"
            />

            <information-card-template
                v-if="chart.type === 'information_card'"
                :chart_selected="local_chart"
                :key="'information-card-' + id"
            />
        </template>

        <!-- Card Footer with Control Buttons -->
        <div v-if="allow_label_toggle || allow_resize || paginationState.isTruncated" class="card-footer d-flex align-items-center justify-content-between gap-2 px-2 py-2 position-relative">
            <!-- Data Labels Toggle Button -->
            <div v-if="allow_label_toggle" class="chart-labels-toggle-container">
                <button 
                    class="btn btn-secondary labels-toggle-btn btn-icon-only-sm" 
                    @click="toggleDataLabels" 
                    data-bs-toggle="tooltip" 
                    :title="effectiveShowDataLabels ? 'Hide Data Labels' : 'Show Data Labels'"
                    :class="{ 'active': effectiveShowDataLabels }">
                    <div class="toggle-knob">
                        <img src="/img/icons/avid-icons/text.svg" alt="Toggle Labels" class="icon pe-0">
                    </div>
                </button>
            </div>

            <!-- Pagination controls centered -->
            <div v-if="paginationState.isTruncated" class="chart-pagination-controls-container d-flex align-items-center gap-2">
                <button 
                    @click="previousPage" 
                    :disabled="paginationState.currentPageStart === 0"
                    class="btn btn-sm btn-secondary"
                    title="Previous">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <span>{{ formattedPaginationText }}</span>
                <button 
                    @click="nextPage" 
                    :disabled="paginationState.currentPageStart + paginationState.displayedDataCount >= paginationState.totalDataLength"
                    class="btn btn-sm btn-secondary"
                    title="Next">
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>

            <!-- Resize / Expand Button -->
            <div v-if="allow_resize" class="chart-expand-container">
                <button class="btn btn-secondary expand-btn btn-icon-only-sm" @click.stop="toggleResizeMenu" data-bs-toggle="tooltip" title="Resize Chart">
                    <img src="/img/icons/avid-icons/expand-size.svg" alt="Resize" class="icon pe-0">
                </button>
                
                <Transition name="fade">
                    <div v-if="showResizeMenu" class="resize-drop-up" @click.stop>
                        <div class="drop-up-header">
                            <h4>Resize Chart</h4>
                            <button class="close-btn" @click="showResizeMenu = false">
                                <img src="/img/icons/avid-icons/close.svg" alt="Close">
                            </button>
                        </div>
                        <div class="size-options">
                            <button v-for="option in sizeOptions" :key="option.value" 
                                    class="size-option" 
                                    :class="{ active: (current_size === option.value || (current_size === 'x-large' && option.value === 'fullsize')) }"
                                    @click="selectSize(option.value)">
                                <div class="option-content">
                                    <img :src="option.icon" :alt="option.label">
                                    <span>{{ option.label }}</span>
                                </div>
                                <img v-if="(current_size === option.value || (current_size === 'x-large' && option.value === 'fullsize'))" src="/img/icons/avid-icons/check-01.svg" class="check-icon" alt="Selected">
                            </button>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
        
        <!-- Deep Dive Modal - Only for charts smaller than x-large -->
        <div class="modal fade" :id="'deepDiveModal-' + local_chart_id" tabindex="-1" :aria-labelledby="'deepDiveModalLabel-' + local_chart_id" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" :id="'deepDiveModalLabel-' + local_chart_id">Chart Definitions</h3>
                        <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="definitions-content">
                            <div class="definition-item" v-if="local_chart.subtitle && local_chart.subtitle != ''">
                                <h5 class="definition-label">What Question Does This Answer:</h5>
                                <p class="definition-value">{{ getFormattedSubtitle(local_chart.subtitle) }}</p>
                            </div>

                            <div class="definition-item" v-if="local_chart.description && local_chart.description != ''">
                                <h5 class="definition-label">Description:</h5>
                                <p class="definition-value">{{ this.local_chart.description }}</p>
                            </div>

                            <div class="definition-item" v-if="getDimensions().length > 0">
                                <h5 class="definition-label">Dimensions:</h5>
                                <p class="definition-value">{{ getDimensions().join(', ') }}</p>
                            </div>  

                            <div class="definition-item" v-if="getMetrics().length > 0">
                                <h5 class="definition-label">Metrics:</h5>
                                <p class="definition-value">{{ getMetrics().join(', ') }}</p>
                            </div>

                            <div class="definition-item" v-if="getFilters().length > 0">
                                <h5 class="definition-label">Filters:</h5>
                                <ul class="definition-list">
                                    <li v-for="(filter, index) in getFilters()" :key="index">{{ filter }}</li>
                                </ul>
                            </div> 
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { marked } from 'marked';
import filters from '../../../mixins/filters';

export default {
    name: 'ChartTemplate',
    mixins: [filters],
    props: {
        chart: {
            type: Object,
            default: () => ({})
        },
        id: {
            type: [String, Number],
            default: null
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        show_title: {
            type: Boolean,
            default: true
        },
        show_data_labels: {
            type: Boolean,
            default: false
        },
        chart_show_data_labels: {
            type: Boolean,
            default: undefined
        },
        page_show_data_labels: {
            type: Boolean,
            default: false
        },
        allow_deep_dive: {
            type: Boolean,
            default: true
        },
        allow_download: {
            type: Boolean,
            default: false
        },
        allow_label_toggle: {
            type: Boolean,
            default: false
        },
        allow_resize: {
            type: Boolean,
            default: false
        },
        current_size: {
            type: String,
            default: 'small'
        },
        page_filters: {
            type: Array,
            default: () => []
        },
        page_dimensions: {
            type: Array,
            default: () => []
        },
        section_filters: {
            type: Array,
            default: () => []
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        /** When on a report page, section-level title override (null = hide title; chart title is ignored) */
        section_title: {
            type: String,
            default: undefined
        },
        is_filter_panel_expanded: {
            type: Boolean,
            default: false
        },
        is_section_active: {
            type: Boolean,
            default: true
        },
        should_show_summary: {
            type: Boolean,
            default: true
        },
        chart_summary: {
            type: Object,
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        hide_xlarge_side_panel: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            local_chart_id: null,
            local_chart: {},
            showResizeMenu: false,
            sidePanelVisible: !this.hide_xlarge_side_panel, // Chart side panel (Avid Overview / Definitions); false = panel hidden, chart uses col-12
            activeTab: 'edna_overview', // For side panel toggle group
            deepDiveModalInstance: null, // Store modal instance
            sizeOptions: [
                { label: 'Small', value: 'small', icon: '/img/icons/avid-icons/size-icon-regular.svg' },
                { label: 'Medium', value: 'medium', icon: '/img/icons/avid-icons/size-icon-medium.svg' },
                { label: 'Large', value: 'large', icon: '/img/icons/avid-icons/size-icon-large.svg' },
                { label: 'Fullsize', value: 'x-large', icon: '/img/icons/avid-icons/size-icon-fullsize.svg' }
            ],
            paginationState: {
                isTruncated: false,
                currentPageStart: 0,
                displayedDataCount: 0,
                totalDataLength: 0,
                paginationText: ''
            },
            /** For edna scorecard cards: true at index i means the description is truncated and should show tooltip */
            descriptionTruncated: []
        };
    },
    computed: {
        /** Title to show in header: on report page use section_title when provided, else chart title */
        displayTitle() {
            if (this.if_on_report_page && this.section_title !== undefined) {
                return this.section_title ?? null;
            }
            return this.local_chart?.title ?? null;
        },
        isChartSummaryLoading() {
            const s = this.chart_summary;
            return s && (s.status === 'queued' || (s.status === 'failed' && !s.summary));
        },
        processedChartSummaryHtml() {
            if (!this.chart_summary || !this.chart_summary.summary) return '';
            let html = this.markdownToHtml(this.chart_summary.summary);
            const relatedCharts = this.chart_summary.related_charts || [];
            if (relatedCharts.length > 0) {
                html = this.processChartReferences(html, relatedCharts);
            }
            return html;
        },
        /**
         * Determines if labels should be shown by default for charts that support smart defaults
         * For large/x-large charts:
         * - column, line, bar_line (time-based): labels shown by default
         * - horizontal_bar, stacked_bar, stacked_column, 100_percent_stacked_bar, 100_percent_stacked_column: labels shown by default
         * However, if chart_show_data_labels is explicitly set (not undefined), respect that
         * If chart_show_data_labels is undefined, use page-level default OR chart-specific default
         */
        effectiveShowDataLabels() {
            // If chart has explicit show_data_labels setting (not undefined), use that
            if (this.chart_show_data_labels !== undefined) {
                return this.chart_show_data_labels;
            }
            
            const chartType = this.local_chart.type;
            const chartSize = this.current_size || this.local_chart?.size || 'small';
            
            // For charts that support smart defaults based on size (large/x-large)
            if (chartSize === 'large' || chartSize === 'x-large') {
                // Time-based charts (column, line, bar_line): show labels if time-based
                if (chartType === 'column' || chartType === 'line' || chartType === 'cumulative_line' || chartType === 'bar_line') {
                    if (this.isChartTimeBased(chartType)) {
                        return true;
                    }
                }
                
                // Bar charts (horizontal_bar, stacked variants): always show labels for large/x-large
                if (chartType === 'horizontal_bar' || 
                    chartType === 'stacked_bar' || 
                    chartType === 'stacked_column' ||
                    chartType === '100_percent_stacked_bar' ||
                    chartType === '100_percent_stacked_column') {
                    return true;
                }
            }
            
            // For all other cases, use page-level default
            return this.page_show_data_labels || this.show_data_labels || false;
        },
        /**
         * Computed property to format pagination text as "Page x of y"
         * where x is the current page index and y is the total page count
         */
        formattedPaginationText() {
            if (!this.paginationState.isTruncated) {
                return '';
            }
            
            const { currentPageStart, displayedDataCount, totalDataLength } = this.paginationState;
            
            // Safety check: avoid division by zero
            if (!displayedDataCount || !totalDataLength) {
                return '';
            }
            
            // Calculate total pages
            const totalPages = Math.ceil(totalDataLength / displayedDataCount);
            
            // When on the last page, next is disabled. Use totalPages as current so we show "Page 3 of 3"
            // not "Page 2 of 3" (which can happen when the last page has fewer than displayedDataCount items)
            const isOnLastPage = currentPageStart + displayedDataCount >= totalDataLength;
            const currentPage = isOnLastPage
                ? totalPages
                : Math.floor(currentPageStart / displayedDataCount) + 1;
            
            return `Page ${currentPage} of ${totalPages}`;
        },
        /**
         * Computed property for Edna Overview scorecard cards
         * Returns array of card objects with title, value, description, icon, etc.
         */
        ednaOverviewCards() {
            if (!this.local_chart?.data || !this.local_chart?.labels || this.if_refreshing) {
                return [];
            }

            const isTimeBased = this.isChartTimeBased();
            const cards = [];

            if (isTimeBased) {
                // Time-based: show most recent year and percent change
                const mostRecentCard = this.getMostRecentYearCard();
                const percentChangeCard = this.getPercentChangeCard();
                
                if (mostRecentCard) cards.push(mostRecentCard);
                if (percentChangeCard) cards.push(percentChangeCard);
            } else {
                // Non-time-based: show largest number and percent of whole
                const largestCard = this.getLargestNumberCard();
                const percentOfWholeCard = this.getPercentOfWholeCard();
                
                if (largestCard) cards.push(largestCard);
                if (percentOfWholeCard) cards.push(percentOfWholeCard);
            }

            return cards;
        }
    },
    watch: {
        chart: {
            handler(new_value) {
                this.local_chart = this.chart;
                // Reset pagination state for new charts
                this.paginationState = {
                    isTruncated: false,
                    currentPageStart: 0,
                    displayedDataCount: 0,
                    totalDataLength: 0,
                    paginationText: ''
                };
                if (this.local_chart.type == 'cumulative_line' && this.local_chart.data) {
                    this.computeCumulativeValues();
                }
            },
            immediate: true
        },
        is_section_active(value) {
            if (!value) {
                this.removeChartTooltip();
            }
        },
        'chart_summary.summary'() {
            if (this.chart_summary?.summary && (this.chart_summary?.related_charts?.length || 0) > 0) {
                this.$nextTick(() => this.attachChartReferenceClickHandlers());
            }
        },
        'chart_summary.related_charts': {
            handler() {
                if (this.chart_summary?.summary && (this.chart_summary?.related_charts?.length || 0) > 0) {
                    this.$nextTick(() => this.attachChartReferenceClickHandlers());
                }
            },
            deep: true
        }
    },
    beforeMount() {
        if (!this.id) {
            this.local_chart_id = this.chart.id;
        } else {
            this.local_chart_id = this.id;
        }
        
        // Set active tab to definitions if summary should be hidden
        if (!this.should_show_summary) {
            this.activeTab = 'definitions';
        }
        this.local_chart = this.chart;
        if (this.local_chart.type == 'cumulative_line' && this.local_chart.data) {
            this.computeCumulativeValues();
        }
    },
    mounted() {
        this.ensureChartHeaderTooltips();
    },
    updated() {
        this.$nextTick(() => {
            this.checkEdnaDescriptionTruncation();
        });
        this.ensureChartHeaderTooltips();
    },
    beforeUnmount() {
        // Clean up any tooltips created by this chart
        this.removeChartTooltip();
        
        // Dispose of modal instance if it exists
        if (this.deepDiveModalInstance) {
            this.deepDiveModalInstance.dispose();
            this.deepDiveModalInstance = null;
        }
        
        // Also try to dispose via DOM element
        const modalId = 'deepDiveModal-' + this.local_chart_id;
        const modalElement = document.getElementById(modalId);
        if (modalElement && window.bootstrap && window.bootstrap.Modal) {
            const existingInstance = window.bootstrap.Modal.getInstance(modalElement);
            if (existingInstance) {
                existingInstance.dispose();
            }
        }
    },
    deactivated() {
        this.removeChartTooltip();
    },
    beforeRouteLeave(to, from, next) {
        this.removeChartTooltip();
        next();
    },
    methods: {
        markdownToHtml(text) {
            if (!text) return text;
            let html = marked.parse(text, { breaks: true });
            // Convert any remaining **bold**, *italic*, and _italic_ that marked didn't parse (e.g. from AI output)
            html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
            html = html.replace(/\*([^*]+?)\*/g, '<em>$1</em>');
            html = html.replace(/_([^_<]+)_/g, '<em>$1</em>');
            html = html.replace(/\*/g, '');
            return html;
        },
        processChartReferences(summary, relatedCharts) {
            if (!summary || !relatedCharts || relatedCharts.length === 0) return summary;
            let processedSummary = summary;
            const chartMap = {};
            relatedCharts.forEach(chart => {
                if (chart.chart_title) chartMap[chart.chart_title] = chart;
            });
            const sortedChartTitles = Object.keys(chartMap).sort((a, b) => b.length - a.length);
            sortedChartTitles.forEach(chartTitle => {
                const chart = chartMap[chartTitle];
                const chartId = parseInt(chart.chart_id, 10);
                const sectionId = parseInt(chart.section_id, 10);
                if (!chartId || !sectionId || chartId <= 0 || sectionId <= 0) return;
                const escapedTitle = chartTitle.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(\\*{0,2})(?<!<a[^>]*>)(?<!["'])\\b${escapedTitle}\\b(?![^<]*</a>)(\\*{0,2})`, 'gi');
                processedSummary = processedSummary.replace(regex, () =>
                    `<a href="#" class="chart-reference-link" data-chart-id="${chartId}" data-section-id="${sectionId}" data-is-same-section="${chart.is_same_section}">${chartTitle}</a>`
                );
            });
            return processedSummary;
        },
        attachChartReferenceClickHandlers() {
            this.$nextTick(() => {
                const el = this.$refs.chartSummaryBody;
                if (!el) return;
                const links = el.querySelectorAll('.chart-reference-link');
                links.forEach(link => {
                    const newLink = link.cloneNode(true);
                    link.parentNode.replaceChild(newLink, link);
                    newLink.addEventListener('click', (e) => {
                        e.preventDefault();
                        const chartId = parseInt(newLink.getAttribute('data-chart-id'), 10);
                        const sectionId = parseInt(newLink.getAttribute('data-section-id'), 10);
                        const isSameSection = newLink.getAttribute('data-is-same-section') === 'true';
                        this.$emit('chartReferenceClick', { chartId, sectionId, isSameSection });
                    });
                });
            });
        },
        downloadChart(event) {
            if (this.local_chart.type === 'table' && this.$refs.tableTemplate) {
                this.$refs.tableTemplate.exportToCSV();
            } else if (this.local_chart.type === 'multi_dimension_table' && this.$refs.multiDimensionTableTemplate) {
                this.$refs.multiDimensionTableTemplate.exportToCSV();
            } else {
                this.$emit('downloadChart');
            }
            this.hideTooltip(event);
        },
        openDeepDiveModal(event) {
            this.hideTooltip(event);
            
            // Close any other open deep dive modals first
            const allModals = document.querySelectorAll('[id^="deepDiveModal-"]');
            allModals.forEach(modalEl => {
                if (modalEl.id !== 'deepDiveModal-' + this.local_chart_id) {
                    const existingModal = window.bootstrap?.Modal?.getInstance(modalEl);
                    if (existingModal) {
                        existingModal.hide();
                    }
                }
            });
            
            // Show the modal using Bootstrap
            const modalId = 'deepDiveModal-' + this.local_chart_id;
            const modalElement = document.getElementById(modalId);
            if (modalElement && window.bootstrap && window.bootstrap.Modal) {
                // Dispose of any existing instance first
                const existingInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (existingInstance) {
                    existingInstance.dispose();
                }
                
                // Create and show new modal instance
                this.deepDiveModalInstance = new window.bootstrap.Modal(modalElement);
                this.deepDiveModalInstance.show();
            }
        },
        toggleDataLabels(event) {
            // Emit the current effective value so parent can toggle it correctly
            this.$emit('toggleDataLabels', {
                event,
                currentValue: this.effectiveShowDataLabels
            });
            this.hideTooltip(event);
        },
        hideTooltip(event) {
            // Hide tooltip and blur button to prevent tooltip from sticking
            if (event && event.currentTarget) {
                const button = event.currentTarget;
                // Hide Bootstrap tooltip if available
                if (window.bootstrap && window.bootstrap.Tooltip) {
                    const tooltip = window.bootstrap.Tooltip.getInstance(button);
                    if (tooltip) {
                        tooltip.hide();
                    }
                }
                // Blur the button to ensure tooltip disappears
                button.blur();
            }
        },
        toggleResizeMenu(event) {
            this.showResizeMenu = !this.showResizeMenu;
            this.hideTooltip(event);
            
            if (this.showResizeMenu) {
                document.addEventListener('click', this.closeResizeMenu);
            } else {
                document.removeEventListener('click', this.closeResizeMenu);
            }
        },
        closeResizeMenu() {
            this.showResizeMenu = false;
            document.removeEventListener('click', this.closeResizeMenu);
        },
        selectSize(size) {
            // Clear all chart tooltips on resize so positions stay correct after layout change
            document.querySelectorAll('[id^="chart-"][id$="-tooltip"]').forEach(el => {
                el.style.opacity = 0;
                el.style.pointerEvents = 'none';
            });
            this.$emit('resizeChart', { size });
            this.showResizeMenu = false;
            document.removeEventListener('click', this.closeResizeMenu);
        },
        exploreChart(data) {
            this.$emit('exploreChart', data);
        },
        computeCumulativeValues() {
            Object.keys(this.local_chart.data).forEach((key) => {
                let dataset_temp = [];
                let temp;
                for (let i = 0; i < this.local_chart.data[key].length; i++) {
                    if (i != 0) {
                        temp = temp + this.local_chart.data[key][i];
                    } else {
                        temp = this.local_chart.data[key][i];
                    }
                    dataset_temp.push(temp);
                }
                this.local_chart.data[key] = [...dataset_temp];
            });
        },
        removeChartTooltip() {
            if (!this.local_chart_id) {
                return;
            }

            // Remove every tooltip generated for this chart, even if section/tab context changed.
            const tooltipPrefix = `chart-${this.local_chart_id}-`;
            document.querySelectorAll(`[id^="${tooltipPrefix}"][id$="-tooltip"]`).forEach((tooltipEl) => {
                tooltipEl.remove();
            });
        },
        replaceDate(text) {
            if (!text || !text.toLowerCase().includes('yesterday')) {
                return text;
            }
            const date = new Date();
            const yesterdayDate = (date.getMonth() + 1).toString() + '/' + (date.getDate() - 1).toString();
            return text.replace(/yesterday/gi, yesterdayDate);
        },
        handlePaginationUpdate(state) {
            this.paginationState = { ...this.paginationState, ...state };
        },
        ensureChartHeaderTooltips() {
            if (typeof window.bootstrap === 'undefined') return;
            this.$nextTick(() => {
                const triggers = this.$el?.querySelectorAll?.('[data-bs-toggle="tooltip"]') || [];
                triggers.forEach((el) => {
                    if (!window.bootstrap.Tooltip.getInstance(el)) {
                        new window.bootstrap.Tooltip(el, { container: 'body' });
                    }
                });
            });
        },
        showSidePanel(event) {
            const button = event?.currentTarget;
            if (button && typeof window.bootstrap !== 'undefined') {
                const tooltip = window.bootstrap.Tooltip.getInstance(button);
                if (tooltip) {
                    tooltip.hide();
                }
            }
            this.sidePanelVisible = true;
        },
        closeSidePanel() {
            const el = this.$refs.closePanelBtn;
            if (el && typeof window.bootstrap !== 'undefined') {
                const tooltip = window.bootstrap.Tooltip.getInstance(el);
                if (tooltip) {
                    tooltip.hide();
                }
            }
            this.sidePanelVisible = false;
        },
        checkEdnaDescriptionTruncation() {
            const refs = this.$refs.ednaDescRefs;
            if (!refs || !this.ednaOverviewCards?.length) {
                this.descriptionTruncated = [];
                return;
            }
            const elList = Array.isArray(refs) ? refs : [refs];
            const next = elList.map((el) => el && el.scrollWidth > el.clientWidth);
            if (next.length !== this.descriptionTruncated.length || next.some((v, i) => v !== this.descriptionTruncated[i])) {
                this.descriptionTruncated = next;
                this.$nextTick(() => this.ensureChartHeaderTooltips());
            }
        },
        previousPage() {
            // Call the previousPage method on the child chart component
            const chartRefs = ['barLineChart', 'columnChart', 'stackedColumnChart', 'lineChart', 'pieChart', 'horizontalBarChart', 'stackedHorizontalBarChart'];
            for (const ref of chartRefs) {
                if (this.$refs[ref] && typeof this.$refs[ref].previousPage === 'function') {
                    this.$refs[ref].previousPage();
                    break;
                }
            }
        },
        nextPage() {
            // Call the nextPage method on the child chart component
            const chartRefs = ['barLineChart', 'columnChart', 'stackedColumnChart', 'lineChart', 'pieChart', 'horizontalBarChart', 'stackedHorizontalBarChart'];
            for (const ref of chartRefs) {
                if (this.$refs[ref] && typeof this.$refs[ref].nextPage === 'function') {
                    this.$refs[ref].nextPage();
                    break;
                }
            }
        },
        getFormattedTitle(title) {
            return this.if_benchmark_report ? this.replaceDate(title) : title;
        },
        getFormattedSubtitle(subtitle) {
            return this.if_benchmark_report ? this.replaceDate(subtitle) : subtitle;
        },
        shouldFilterOutText(item) {
            const text = typeof item === 'object' && item.text 
                ? String(item.text).toLowerCase() 
                : String(item).toLowerCase();
            return text.includes('null') || text.includes('dummy');
        },
        /**
         * Build display label: "name - description" when description exists, else "name".
         */
        formatFieldLabel(name, description) {
            const n = name != null && String(name).trim() !== '' ? String(name).trim() : '';
            const d = description != null && String(description).trim() !== '' ? String(description).trim() : '';
            if (d === '') return n;
            return n === '' ? d : `${n} - ${d}`;
        },
        getMetrics() {
            if (!this.local_chart.metrics || !Array.isArray(this.local_chart.metrics)) {
                return [];
            }
            return this.local_chart.metrics
                .filter(metric => !this.shouldFilterOutText(metric))
                .map(metric => {
                    if (typeof metric === 'object' && metric.text) {
                        const desc = metric.description || metric.user_description || '';
                        return this.formatFieldLabel(metric.text, desc);
                    }
                    return String(metric);
                });
        },
        getDimensions() {
            if (!this.local_chart.group_by || !Array.isArray(this.local_chart.group_by)) {
                return [];
            }
            // For multi-dimension-table, only show the currently active dimension
            if (this.local_chart.type === 'multi_dimension_table') {
                const selectedDimension = this.$refs.multiDimensionTableTemplate?.selected_dimension;
                if (selectedDimension) {
                    return [selectedDimension];
                }
            }
            return this.local_chart.group_by
                .filter(dimension => !this.shouldFilterOutText(dimension))
                .map(dimension => {
                    if (typeof dimension === 'object' && dimension.text) {
                        const desc = dimension.description || dimension.user_description || '';
                        return this.formatFieldLabel(dimension.text, desc);
                    }
                    return String(dimension);
                });
        },
        getFilters() {
            const allFilters = [];
            const seenFilters = new Set(); // Track duplicates by full display string
            const seenFilterNames = new Set(); // Track by label so chart + page dimension same filter shows once (prefer user-friendly)

            /**
             * Format a single ISO date string for display (UTC date as MM/DD/YYYY).
             */
            const formatDateForFilter = (dateString) => {
                if (!dateString || typeof dateString !== 'string') return '';
                const date = new Date(dateString.trim());
                if (Number.isNaN(date.getTime())) return '';
                const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
                const dd = String(date.getUTCDate()).padStart(2, '0');
                const yyyy = date.getUTCFullYear();
                return `${mm}/${dd}/${yyyy}`;
            };

            /**
             * Format a date string as month label (UTC): "MMM yyyy" (e.g. Jan 2019).
             * Accepts ISO full timestamp or YYYY-MM.
             */
            const formatMonthForFilter = (dateString) => {
                if (!dateString || typeof dateString !== 'string') return '';
                const date = new Date(dateString.trim());
                if (Number.isNaN(date.getTime())) return '';
                const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
                const year = date.getUTCFullYear();
                return `${month} ${year}`;
            };

            /**
             * Format filter value for display when field_type is unknown: ISO timestamps and SQL date ranges.
             */
            const formatFilterValue = (value) => {
                if (typeof value !== 'string' || !value.trim()) return value;
                const trimmed = value.trim();
                // SQL BETWEEN TIMESTAMP('...') AND TIMESTAMP('...')
                const betweenMatch = trimmed.match(/TIMESTAMP\s*\(\s*['"]([^'"]+)['"]\s*\)/g);
                if (betweenMatch && betweenMatch.length >= 2) {
                    const start = formatDateForFilter(betweenMatch[0].replace(/TIMESTAMP\s*\(\s*['"]([^'"]+)['"]\s*\)/, '$1'));
                    const end = formatDateForFilter(betweenMatch[1].replace(/TIMESTAMP\s*\(\s*['"]([^'"]+)['"]\s*\)/, '$1'));
                    if (start && end) return `${start} - ${end}`;
                }
                // ISO range: "2019-01-01T00:00:00.000Z - 2022-12-31T00:00:00.000Z"
                const rangeMatch = trimmed.match(/^(.+?)\s+-\s+(.+)$/);
                if (rangeMatch) {
                    const start = formatDateForFilter(rangeMatch[1].trim());
                    const end = formatDateForFilter(rangeMatch[2].trim());
                    if (start && end) return `${start} - ${end}`;
                }
                // Single ISO date
                const single = formatDateForFilter(trimmed);
                if (single) return single;
                return value;
            };

            /**
             * Format filter value by AdvancedFilterDropdown field type (date_range, month_range, month_picker, numeric_slider, multi_select, single_select, etc.).
             */
            const formatFilterValueByType = (value, fieldType) => {
                if (fieldType == null || fieldType === '') {
                    return value;
                }
                const type = String(fieldType).toLowerCase();

                if (type === 'date_range') {
                    let start = '';
                    let end = '';
                    if (value && typeof value === 'object' && ('start_date' in value || 'end_date' in value)) {
                        start = formatDateForFilter(value.start_date || '');
                        end = formatDateForFilter(value.end_date || '');
                    } else if (typeof value === 'string') {
                        const rangeMatch = value.trim().match(/^(.+?)\s+-\s+(.+)$/);
                        if (rangeMatch) {
                            start = formatDateForFilter(rangeMatch[1].trim());
                            end = formatDateForFilter(rangeMatch[2].trim());
                        }
                    }
                    if (start || end) return `${start} - ${end}`.replace(/^\s*-\s*|\s*-\s*$/g, '').trim();
                }

                if (type === 'month_range') {
                    let start = '';
                    let end = '';
                    if (value && typeof value === 'object' && ('start_date' in value || 'end_date' in value)) {
                        start = formatMonthForFilter(value.start_date || '');
                        end = formatMonthForFilter(value.end_date || '');
                    } else if (typeof value === 'string') {
                        const rangeMatch = value.trim().match(/^(.+?)\s+-\s+(.+)$/);
                        if (rangeMatch) {
                            start = formatMonthForFilter(rangeMatch[1].trim());
                            end = formatMonthForFilter(rangeMatch[2].trim());
                        }
                    }
                    if (start || end) return `${start} - ${end}`.replace(/^\s*-\s*|\s*-\s*$/g, '').trim();
                }

                if (type === 'month_picker') {
                    if (Array.isArray(value) && value.length > 0) {
                        return value.map(v => (typeof v === 'string' ? v : String(v))).join(', ');
                    }
                }

                if (type === 'numeric_slider') {
                    if (Array.isArray(value) && value.length >= 2) {
                        return `${value[0]} - ${value[1]}`;
                    }
                }

                if (type === 'multi_select' || type === 'array_multi_select') {
                    if (Array.isArray(value) && value.length > 0) {
                        return value.map(v => (typeof v === 'string' ? v : String(v))).join(', ');
                    }
                }

                if (type === 'single_select' || type === 'array_single_select') {
                    if (Array.isArray(value) && value.length > 0) {
                        return value[0] != null ? String(value[0]) : '';
                    }
                    if (value != null && value !== '') return String(value);
                }

                return value;
            };
            
            /**
             * Helper function to format a filter and add it to the list if not duplicate
             */
            const isSqlStyleFilterValue = (value) => {
                if (typeof value !== 'string') return false;
                return (value.includes('CAST(') && value.includes('AS STRING)')) || value.includes(' IN (');
            };

            const addFilter = (filterName, filterValue = null, fieldType = null, filterDescription = null) => {
                if (!filterName) return;
                
                // For benchmark reports: never add Vertical or Annual Revenue Range when value is SQL-style,
                // so only the user-friendly version from page dimensions is shown
                if (this.if_benchmark_report && filterValue != null && typeof filterValue === 'string') {
                    const nameNorm = String(filterName).trim().toLowerCase().replace(/:$/, '');
                    if ((nameNorm === 'vertical' || nameNorm === 'annual revenue range') && isSqlStyleFilterValue(filterValue)) {
                        return;
                    }
                }
                
                const displayName = this.formatFieldLabel(filterName, filterDescription || '');
                let displayStr;
                if (filterValue != null && filterValue !== '') {
                    let displayValue;
                    if (fieldType != null && fieldType !== '') {
                        const typeFormatted = formatFilterValueByType(filterValue, fieldType);
                        displayValue = typeFormatted != null && typeFormatted !== '' ? typeFormatted : filterValue;
                    } else if (Array.isArray(filterValue)) {
                        displayValue = filterValue.map(v => typeof v === 'string' ? formatFilterValue(v) : v).join(', ');
                    } else {
                        displayValue = typeof filterValue === 'string' ? formatFilterValue(filterValue) : filterValue;
                    }
                    displayStr = `${displayName}: ${displayValue}`;
                } else {
                    displayStr = displayName;
                }
                
                // Deduplicate by full display string
                if (seenFilters.has(displayStr)) {
                    return;
                }
                // Deduplicate by filter label: same filter from chart (SQL-style) and page dimensions (user-friendly) shows once; prefer first-added (page dimensions run first)
                if (seenFilterNames.has(displayName)) {
                    return;
                }
                seenFilters.add(displayStr);
                seenFilterNames.add(displayName);
                allFilters.push(displayStr);
            };
            
            /**
             * Helper function to format filter from chart filter format
             */
            const formatChartFilter = (filter) => {
                if (typeof filter !== 'object') {
                    addFilter(String(filter));
                    return;
                }
                
                const filterName = filter.text || filter.name || filter.filter_name || '';
                const filterValue = filter.value || filter.filter_value || '';
                const filterDescription = filter.description || filter.user_description || filter.filter_description || '';
                
                // Filter out objects where text contains "null" or "dummy" (case-insensitive)
                if (this.shouldFilterOutText({ text: filterName })) {
                    return;
                }
                
                // For benchmark reports: skip SQL-formatted Vertical and Annual Revenue Range
                // so only the user-friendly version from page dimensions is shown
                if (this.if_benchmark_report && filterName && typeof filterValue === 'string') {
                    const nameNorm = String(filterName).trim().toLowerCase().replace(/:$/, '');
                    if ((nameNorm === 'vertical' || nameNorm === 'annual revenue range') && isSqlStyleFilterValue(filterValue)) {
                        return;
                    }
                }
                
                // Filter out "Donations Only" and "Last 5 Years" by name, but keep "Last 5 Periods"
                if (filterName === 'Donations Only' || filterName === 'Last 5 Years') {
                    return;
                }
                
                // Also check filter value for "Donations Only" (SQL fragment)
                if (typeof filterValue === 'string' && filterValue.includes("CAST(IF(transactions._is_donation,\"Donation\",\"Non-Donation\") AS STRING) ='Donation'")) {
                    return;
                }
                
                // Note: We don't filter by value = 36 anymore since that could be "Last 5 Periods" which should stay
                
                if (Array.isArray(filterValue)) {
                    addFilter(filterName, filterValue.join(', '), null, filterDescription);
                } else if (filterValue) {
                    // Check if value is an integer (filter table ID)
                    if (Number.isInteger(filterValue) || (typeof filterValue === 'string' && /^\d+$/.test(filterValue))) {
                        // For integer IDs, only display the filter name
                        addFilter(filterName, null, null, filterDescription);
                    } else if (typeof filterValue === 'string') {
                        // Try to extract value from SQL condition like: CAST(...) ='value' or ='value'
                        const conditionMatch = filterValue.match(/=\s*['"]([^'"]+)['"]/);
                        if (conditionMatch && conditionMatch[1]) {
                            addFilter(filterName, conditionMatch[1], null, filterDescription);
                        } else {
                            addFilter(filterName, filterValue, null, filterDescription);
                        }
                    } else {
                        addFilter(filterName, filterValue, null, filterDescription);
                    }
                } else {
                    addFilter(filterName, null, null, filterDescription);
                }
            };

            // 1. Page dimensions (user-selected filters from URL) — run first so user-friendly values win over chart SQL-style definitions
            if (this.page_dimensions && Array.isArray(this.page_dimensions) && this.page_dimensions.length > 0) {
                const currentChartId = this.id != null ? String(this.id).trim() : (this.local_chart_id != null ? String(this.local_chart_id).trim() : '');
                const sourceChartId = this.applied_chart_filter?.if_chart_filter_applied && this.applied_chart_filter?.section_chart_id != null
                    ? String(this.applied_chart_filter.section_chart_id).trim()
                    : '';
                const isSourceChart = currentChartId !== '' && sourceChartId !== '' && currentChartId === sourceChartId;

                this.page_dimensions.forEach(dimension => {
                    const hasValue = (dimension.value && (
                        (Array.isArray(dimension.value) && dimension.value.length > 0) ||
                        (typeof dimension.value === 'object' && (dimension.value.start_date || dimension.value.end_date)) ||
                        (typeof dimension.value === 'string' && dimension.value !== '') ||
                        (typeof dimension.value !== 'object' && dimension.value !== null)
                    )) || (dimension.default_value && (
                        (Array.isArray(dimension.default_value) && dimension.default_value.length > 0) ||
                        (typeof dimension.default_value === 'object' && (dimension.default_value.start_date || dimension.default_value.end_date)) ||
                        (typeof dimension.default_value === 'string' && dimension.default_value !== '') ||
                        (typeof dimension.default_value !== 'object' && dimension.default_value !== null)
                    ));

                    if (!hasValue) return;

                    const value = dimension.value || dimension.default_value;
                    if (Array.isArray(value) && value.length === 1 && value[0] === 'Donation') {
                        return;
                    }

                    const filterName = dimension.label || dimension.name || '';
                    if (!filterName) return;

                    if (isSourceChart) {
                        if (filterName === 'Chart Filter' && dimension.id < 0) {
                            return;
                        }
                        if (this.applied_chart_filter?.chart_filters && Array.isArray(this.applied_chart_filter.chart_filters)) {
                            const matchesChartFilter = this.applied_chart_filter.chart_filters.some(cf =>
                                cf.column === dimension.column &&
                                (cf.value === dimension.value || JSON.stringify(cf.value) === JSON.stringify(dimension.value))
                            );
                            if (matchesChartFilter) {
                                return;
                            }
                        }
                    }

                    if (dimension.field_type === 'date_range' || dimension.field_type === 'month_range') {
                        if (value && typeof value === 'object' && (value.start_date || value.end_date)) {
                            addFilter(filterName, value, dimension.field_type);
                        }
                    } else if (dimension.field_type === 'numeric_slider' && Array.isArray(value)) {
                        addFilter(filterName, value, dimension.field_type);
                    } else if (dimension.field_type === 'month_picker' && Array.isArray(value)) {
                        addFilter(filterName, value, dimension.field_type);
                    } else if (dimension.field_type === 'multi_select' || dimension.field_type === 'array_multi_select') {
                        if (Array.isArray(value) && value.length > 0) {
                            addFilter(filterName, value, dimension.field_type);
                        } else {
                            addFilter(filterName);
                        }
                    } else if (dimension.field_type === 'single_select' || dimension.field_type === 'array_single_select') {
                        if (value != null && value !== '') {
                            addFilter(filterName, value, dimension.field_type);
                        } else {
                            addFilter(filterName);
                        }
                    } else if (Array.isArray(value)) {
                        addFilter(filterName, value.join(', '));
                    } else if (value) {
                        if (Number.isInteger(value) || (typeof value === 'string' && /^\d+$/.test(value))) {
                            addFilter(filterName);
                        } else {
                            addFilter(filterName, value);
                        }
                    } else {
                        addFilter(filterName);
                    }
                });
            }

            // 2. Chart filters (skipped when same label already added from page dimensions)
            if (this.local_chart.filter && Array.isArray(this.local_chart.filter)) {
                this.local_chart.filter.forEach(filter => formatChartFilter(filter));
            }

            // 3. Section filters
            if (this.section_filters && Array.isArray(this.section_filters) && this.section_filters.length > 0) {
                this.section_filters.forEach(filter => {
                    const filterName = filter.name || filter.admin_name || filter.text;
                    const filterDescription = filter.description || filter.user_description || '';
                    if (!filterName) return;
                    if (filterName === 'Donations Only' || filterName === 'Last 5 Years') return;
                    addFilter(filterName, null, null, filterDescription);
                });
            }
            
            // 4. Page filters
            if (this.page_filters && Array.isArray(this.page_filters) && this.page_filters.length > 0) {
                this.page_filters.forEach(filter => {
                    const filterName = filter.name || filter.admin_name || filter.text;
                    const filterDescription = filter.description || filter.user_description || '';
                    if (!filterName) return;
                    if (filterName === 'Donations Only' || filterName === 'Last 5 Years') return;
                    addFilter(filterName, null, null, filterDescription);
                });
            }

            // 5. Applied chart filter (only show on charts that are NOT the source chart)
            if (this.applied_chart_filter && 
                this.applied_chart_filter.if_chart_filter_applied && 
                this.applied_chart_filter.chart_filters && 
                Array.isArray(this.applied_chart_filter.chart_filters) &&
                this.applied_chart_filter.chart_filters.length > 0) {
                
                // Check if this chart is the source chart (the one used to apply the filter)
                // The id prop on ChartTemplateNew (which is chart.id) matches section_chart_id
                // Compare both as strings to handle type mismatches (number vs string)
                const currentChartId = this.id != null ? String(this.id).trim() : (this.local_chart_id != null ? String(this.local_chart_id).trim() : '');
                const sourceChartId = this.applied_chart_filter.section_chart_id != null 
                    ? String(this.applied_chart_filter.section_chart_id).trim() 
                    : '';
                
                // Only show applied chart filter on charts that are NOT the source chart
                // If IDs match, this IS the source chart, so skip showing the filter
                if (currentChartId !== '' && sourceChartId !== '' && currentChartId === sourceChartId) {
                    // This IS the source chart - skip adding the applied chart filter
                    // (Already filtered out from page_dimensions in section 1)
                } else if (currentChartId !== '' && sourceChartId !== '') {
                    // This is NOT the source chart, show the applied chart filter
                    this.applied_chart_filter.chart_filters.forEach(chartFilter => {
                        // Format the chart filter
                        // chartFilter has: column, value
                        // Try to get a readable label and field_type from page dimensions or use column name
                        let columnLabel = 'Chart Filter';
                        let fieldType = null;
                        
                        if (this.page_dimensions && Array.isArray(this.page_dimensions)) {
                            const matchingDimension = this.page_dimensions.find(dim => dim.column === chartFilter.column);
                            if (matchingDimension) {
                                if (matchingDimension.label) columnLabel = matchingDimension.label;
                                if (matchingDimension.field_type) fieldType = matchingDimension.field_type;
                            } else if (chartFilter.column) {
                                columnLabel = chartFilter.column;
                            }
                        } else if (chartFilter.column) {
                            columnLabel = chartFilter.column;
                        }
                        
                        const filterValue = chartFilter.value;
                        
                        if (filterValue) {
                            addFilter(columnLabel, filterValue, fieldType);
                        } else {
                            addFilter(columnLabel);
                        }
                    });
                }
            }
            
            return allFilters;
        },
        /**
         * Checks if a chart is time-based by examining the first group_by dimension only.
         * Returns true only when the primary grouping is time (year, month, date, day).
         */
        isChartTimeBased(chartType) {
            const groupByArray = this.local_chart?.group_by;
            if (!groupByArray || !Array.isArray(groupByArray) || groupByArray.length === 0) {
                return false;
            }

            const groupBy = groupByArray[0];
            if (!groupBy) return false;

            const groupByText = typeof groupBy === 'object' && groupBy.text != null
                ? String(groupBy.text).toLowerCase()
                : (typeof groupBy === 'object' && groupBy.text != null
                    ? String(groupBy.text).toLowerCase()
                    : String(groupBy).toLowerCase());

            return groupByText.includes('year') ||
                groupByText.includes('month') ||
                groupByText.includes('date') ||
                groupByText.includes('day');
        },
        /**
         * Format a value based on format type, respecting user preferences (show_compressed_numbers)
         * Similar to formatDataLabelValue from chart_base mixin
         */
        formatCardValue(value, formatType) {
            if (value == null) return 'N/A';

            // Check if compressed numbers should be used (default to true for backward compatibility)
            const useCompressed = this.show_compressed_numbers !== false;

            // Calculate max_value from chart data for number formatting decisions
            const dataKeys = Object.keys(this.local_chart.data || {});
            let maxValue = 0;
            if (dataKeys.length > 0) {
                dataKeys.forEach(key => {
                    const dataArray = this.local_chart.data[key] || [];
                    dataArray.forEach(val => {
                        if (val != null && val > maxValue) {
                            maxValue = val;
                        }
                    });
                });
            }

            // Format based on type
            if (formatType === 'money') {
                if (useCompressed && value >= 1000) {
                    return this.currency_abbr(value);
                }
                return this.currency_with_zero(value);
            }

            if (formatType === 'number') {
                if (useCompressed && value >= 1000) {
                    return this.num_abbr(value);
                }
                if (maxValue >= 10) {
                    return this.number_with_zero(value);
                }
                return this.number_with_decimal(value);
            }

            if (formatType === 'percent') {
                return this.percentage(value);
            }

            return String(value);
        },
        /**
         * Parse a numeric year from a data key (e.g. "2025 (Nov-Oct)" -> 2025, "FYTD26" -> 2026)
         */
        parseYearFromDataKey(key) {
            const str = String(key);
            const fourDigit = str.match(/(\d{4})/);
            if (fourDigit) return parseInt(fourDigit[1], 10);
            const fytd = str.match(/FYTD(\d{2})/i);
            if (fytd) return 2000 + parseInt(fytd[1], 10);
            return null;
        },
        /**
         * Get icon src and class for overview cards (non–Percent Change) based on value format.
         * Currency ($), percentage (%), or number (default).
         */
        getCardIconForValue(value) {
            const str = String(value ?? '').trim();
            if (str.startsWith('$')) {
                return { iconSrc: '/img/icons/avid-icons/dollar-circle.svg', iconClass: 'icon-currency' };
            }
            if (str.endsWith('%')) {
                return { iconSrc: '/img/icons/avid-icons/percentage-circle.svg', iconClass: 'icon-percentage' };
            }
            return { iconSrc: '/img/icons/avid-icons/number-circle.svg', iconClass: 'icon-number' };
        },
        /**
         * Get card data for most recent year (time-based charts)
         */
        getMostRecentYearCard() {
            const labels = this.local_chart.labels || [];
            const dataKeys = Object.keys(this.local_chart.data || {});
            
            if (labels.length === 0 || dataKeys.length === 0) return null;

            let mostRecentYear = null;
            let mostRecentValue = null;
            let mostRecentYearKey = null;
            let mostRecentPeriod = null;

            // Check if years are in data keys (multi-series charts like line charts)
            // Keys may be "2025 (Nov-Oct)", "FYTD26", "FYTD25", etc.
            const yearsFromKeys = dataKeys.map(key => {
                const year = this.parseYearFromDataKey(key);
                return year != null ? { year, key } : null;
            }).filter(y => y !== null);

            if (yearsFromKeys.length > 0) {
                // Years are in data keys (multi-series chart)
                yearsFromKeys.sort((a, b) => b.year - a.year);
                const mostRecent = yearsFromKeys[0];
                mostRecentYear = mostRecent.year;
                mostRecentYearKey = mostRecent.key;
                
                const seriesData = this.local_chart.data[mostRecentYearKey];
                if (seriesData && seriesData.length > 0) {
                    mostRecentValue = seriesData[seriesData.length - 1];
                    if (labels.length > 0) {
                        mostRecentPeriod = labels[labels.length - 1];
                    }
                }
            } else {
                // Fallback: multiple series but no parseable year in keys — use last series and last period
                const lastKey = dataKeys[dataKeys.length - 1];
                const seriesData = this.local_chart.data[lastKey];
                if (seriesData && seriesData.length > 0 && labels.length > 0) {
                    mostRecentValue = seriesData[seriesData.length - 1];
                    mostRecentPeriod = labels[labels.length - 1];
                    mostRecentYearKey = lastKey;
                    mostRecentYear = new Date().getFullYear();
                }
            }

            if (yearsFromKeys.length === 0 && mostRecentValue == null) {
                // Years in labels (single-series chart grouped by year)
                const yearPattern = /(\d{4})/;
                const years = labels.map(label => {
                    const match = String(label).match(yearPattern);
                    return match ? parseInt(match[1], 10) : null;
                }).filter(y => y !== null);

                if (years.length > 0) {
                    mostRecentYear = Math.max(...years);
                    const mostRecentIndex = labels.findIndex(label => {
                        const match = String(label).match(yearPattern);
                        return match && parseInt(match[1], 10) === mostRecentYear;
                    });

                    if (mostRecentIndex !== -1) {
                        const firstMetricData = this.local_chart.data[dataKeys[0]];
                        mostRecentValue = firstMetricData?.[mostRecentIndex];
                        mostRecentPeriod = labels[mostRecentIndex];
                    }
                }
            }

            if (mostRecentValue == null) return null;
            if (mostRecentYear == null) mostRecentYear = new Date().getFullYear();

            // Format the value based on metric format, respecting user preferences
            const formatType = this.local_chart.metric_formats?.[0] || 'number';
            const formattedValue = this.formatCardValue(mostRecentValue, formatType);

            // Build description: period only (e.g. "Mar", "FYTD26") — year omitted per design
            const description = mostRecentPeriod || String(mostRecentYear);
            const icon = this.getCardIconForValue(formattedValue);

            return {
                title: 'Most Recent',
                value: formattedValue,
                description: description,
                iconSrc: icon.iconSrc,
                iconClass: icon.iconClass
            };
        },
        /**
         * Get card data for percent change (time-based charts)
         * When multiple years exist: compares same period year-over-year (e.g., Feb 2026 vs Feb 2025).
         * When only one series: compares last two periods (e.g., Jan to Feb).
         */
        getPercentChangeCard() {
            const labels = this.local_chart.labels || [];
            const dataKeys = Object.keys(this.local_chart.data || {});
            
            if (labels.length < 2 || dataKeys.length === 0) return null;

            let currentValue = null;
            let previousValue = null;
            let currentPeriod = null;
            let previousPeriod = null;

            // Check if years are in data keys (multi-series charts; keys may be "2025 (Nov-Oct)", "FYTD26", etc.)
            const yearsFromKeys = dataKeys.map(key => {
                const year = this.parseYearFromDataKey(key);
                return year != null ? { year, key } : null;
            }).filter(y => y !== null);

            if (yearsFromKeys.length >= 2) {
                // Multiple years: compare same period year-over-year (e.g., Feb 2026 vs Feb 2025)
                yearsFromKeys.sort((a, b) => b.year - a.year);
                const mostRecent = yearsFromKeys[0];
                const previousYear = yearsFromKeys[1];
                const mostRecentSeriesData = this.local_chart.data[mostRecent.key];
                const previousYearSeriesData = this.local_chart.data[previousYear.key];

                if (!mostRecentSeriesData || mostRecentSeriesData.length === 0) return null;

                const periodIndex = mostRecentSeriesData.length - 1;
                if (periodIndex < 0 || (previousYearSeriesData && periodIndex >= previousYearSeriesData.length)) return null;

                currentValue = mostRecentSeriesData[periodIndex];
                previousValue = previousYearSeriesData?.[periodIndex] ?? null;
                if (previousValue == null) return null;

                const periodLabel = labels[periodIndex];
                currentPeriod = periodLabel ? `${periodLabel} ${mostRecent.year}` : null;
                previousPeriod = periodLabel ? `${periodLabel} ${previousYear.year}` : null;
            } else if (yearsFromKeys.length === 1) {
                // Single year in keys: compare last two periods within that year (e.g., Jan to Feb)
                yearsFromKeys.sort((a, b) => b.year - a.year);
                const mostRecent = yearsFromKeys[0];
                const mostRecentSeriesData = this.local_chart.data[mostRecent.key];

                if (!mostRecentSeriesData || mostRecentSeriesData.length < 2) return null;

                currentValue = mostRecentSeriesData[mostRecentSeriesData.length - 1];
                previousValue = mostRecentSeriesData[mostRecentSeriesData.length - 2];
                if (labels.length >= 2) {
                    currentPeriod = labels[labels.length - 1];
                    previousPeriod = labels[labels.length - 2];
                }
            } else {
                // No parseable year in keys: use last series (treat as most recent) or single series
                const seriesKey = dataKeys[dataKeys.length - 1];
                const seriesData = this.local_chart.data[seriesKey];
                
                if (!seriesData || seriesData.length < 2) return null;
                
                currentValue = seriesData[seriesData.length - 1];
                previousValue = seriesData[seriesData.length - 2];
                currentPeriod = labels[labels.length - 1];
                previousPeriod = labels[labels.length - 2];
            }

            if (currentValue == null || previousValue == null || previousValue === 0) return null;

            const percentChange = (currentValue - previousValue) / previousValue;
            const isIncrease = percentChange >= 0;
            const formattedPercent = this.percentage(percentChange);
            const valueWithSign = percentChange > 0 ? `+${formattedPercent}` : formattedPercent;

            // Build description: e.g., "Sep to Oct" or "2024 to 2025"
            const description = previousPeriod && currentPeriod 
                ? `${previousPeriod} to ${currentPeriod}`
                : 'Period Change';

            return {
                title: 'Percent Change',
                value: valueWithSign,
                description: description,
                iconSrc: '/img/icons/avid-icons/percentage-circle.svg',
                iconClass: isIncrease ? 'icon-increase' : 'icon-decrease'
            };
        },
        /**
         * Get card data for largest value (non-time-based charts).
         * Finds the single highest value across all series and all categories (e.g. "General/Misc FYTD24" -> $20.2M).
         */
        getLargestNumberCard() {
            const labels = this.local_chart.labels || [];
            const dataKeys = Object.keys(this.local_chart.data || {});
            if (labels.length === 0 || dataKeys.length === 0) return null;

            let maxValue = -Infinity;
            let maxLabelIndex = -1;
            let maxSeriesKey = null;

            dataKeys.forEach(seriesKey => {
                const seriesData = this.local_chart.data[seriesKey];
                if (!seriesData || !Array.isArray(seriesData)) return;
                seriesData.forEach((value, index) => {
                    if (value != null && value > maxValue) {
                        maxValue = value;
                        maxLabelIndex = index;
                        maxSeriesKey = seriesKey;
                    }
                });
            });

            if (maxLabelIndex === -1 || maxSeriesKey == null) return null;

            const categoryLabel = labels[maxLabelIndex] || 'N/A';
            const description = dataKeys.length > 1
                ? `${categoryLabel} ${maxSeriesKey}`
                : String(categoryLabel);

            const formatType = this.local_chart.metric_formats?.[0] || 'number';
            const formattedValue = this.formatCardValue(maxValue, formatType);
            const icon = this.getCardIconForValue(formattedValue);

            return {
                title: 'Largest Value',
                value: formattedValue,
                description: description,
                iconSrc: icon.iconSrc,
                iconClass: icon.iconClass
            };
        },
        /**
         * Get card data for percent of whole (non-time-based charts).
         * For each category (label), sums values across all series (e.g. all FYTDs);
         * the category with the largest total is shown as a percentage of the chart grand total.
         */
        getPercentOfWholeCard() {
            const labels = this.local_chart.labels || [];
            const dataKeys = Object.keys(this.local_chart.data || {});
            if (labels.length === 0 || dataKeys.length === 0) return null;

            // Sum by category (label index): groupTotal[i] = sum of all series values at index i
            const groupTotals = labels.map(() => 0);
            let grandTotal = 0;

            dataKeys.forEach(seriesKey => {
                const seriesData = this.local_chart.data[seriesKey];
                if (!seriesData || !Array.isArray(seriesData)) return;
                seriesData.forEach((value, index) => {
                    if (value != null && index < groupTotals.length) {
                        groupTotals[index] += value;
                        grandTotal += value;
                    }
                });
            });

            if (grandTotal === 0) return null;

            // Category with the largest total
            let maxGroupTotal = -Infinity;
            let maxIndex = -1;
            groupTotals.forEach((total, index) => {
                if (total > maxGroupTotal) {
                    maxGroupTotal = total;
                    maxIndex = index;
                }
            });

            if (maxIndex === -1) return null;

            const percentOfWhole = maxGroupTotal / grandTotal;
            const maxLabel = labels[maxIndex] || 'N/A';
            const formattedValue = this.percentage(percentOfWhole);
            const icon = this.getCardIconForValue(formattedValue);

            return {
                title: 'Percent of Universe',
                value: formattedValue,
                description: String(maxLabel),
                iconSrc: icon.iconSrc,
                iconClass: icon.iconClass
            };
        }
    }
}
</script>
